export type StockOptionMatchHighlight = {
  field: 'ticker' | 'company' | 'word-start';
  length: number;
  wordIndex?: number;
};

export interface NormalizedStockOption {
  original: string;
  ticker: string | null;
  company: string;
  display: string;
  value: string;
  matchHighlight?: StockOptionMatchHighlight;
}

const stripTicker = (raw: string): string =>
  raw.replace(/^\$/, '').trim();

const sanitizeCompany = (company: string, fallback: string): string =>
  (company || fallback).trim();

export const normalizeStockOption = (option: string): NormalizedStockOption => {
  try {
    // Validate input
    if (typeof option !== 'string') {
      console.warn('normalizeStockOption received non-string input:', option);
      return {
        original: String(option),
        ticker: null,
        company: '',
        display: '',
        value: ''
      };
    }

    const trimmed = option.trim();

    if (!trimmed) {
      return {
        original: option,
        ticker: null,
        company: '',
        display: '',
        value: ''
      };
    }

    const normalizedMatch = trimmed.match(/^\$([A-Za-z0-9.-]+):\s*(.+)$/);
    if (normalizedMatch) {
      const [, rawTicker, rawName] = normalizedMatch;
      const ticker = stripTicker(rawTicker).toUpperCase();
      const company = sanitizeCompany(rawName, trimmed);
      const display = `$${ticker}: ${company}`;
      return {
        original: option,
        ticker,
        company,
        display,
        value: display
      };
    }

    const leadingTickerMatch = trimmed.match(/^\(([^)]+)\)\s*(.+)$/);
    if (leadingTickerMatch) {
      const [, rawTicker, rawName] = leadingTickerMatch;
      const ticker = stripTicker(rawTicker).toUpperCase();
      const company = sanitizeCompany(
        rawName.replace(/^[:–—-]\s*/, ''),
        trimmed
      );
      const display = `$${ticker}: ${company}`;
      return {
        original: option,
        ticker,
        company,
        display,
        value: display
      };
    }

    const trailingTickerMatch = trimmed.match(/^(.*?)[\s:-]*\(([^)]+)\)\s*$/);
    if (trailingTickerMatch) {
      const [, rawName, rawTicker] = trailingTickerMatch;
      const ticker = stripTicker(rawTicker).toUpperCase();
      const company = sanitizeCompany(
        rawName.replace(/[\s:-]+$/, ''),
        trimmed
      );
      const display = `$${ticker}: ${company}`;
      return {
        original: option,
        ticker,
        company,
        display,
        value: display
      };
    }

    const tickerOnlyMatch = trimmed.match(/^\$?([A-Za-z0-9.-]+)$/);
    if (tickerOnlyMatch) {
      const ticker = stripTicker(tickerOnlyMatch[1]).toUpperCase();
      const display = `$${ticker}: ${ticker}`;
      return {
        original: option,
        ticker,
        company: ticker,
        display,
        value: display
      };
    }

    const fallbackCompany = sanitizeCompany('', trimmed);
    return {
      original: option,
      ticker: null,
      company: fallbackCompany,
      display: fallbackCompany,
      value: fallbackCompany
    };
  } catch (error) {
    console.error('Error normalizing stock option:', error, 'Input:', option);
    // Return safe fallback on any error
    return {
      original: String(option),
      ticker: null,
      company: String(option),
      display: String(option),
      value: String(option)
    };
  }
};
